import { useState, useEffect, useCallback } from 'react';

import { useEventfy } from '@hooks';
import { selectLobbyBanners } from '@hooks/useEventfy/selectors';

import * as S from './styles';

type BannerSlideProps = {
  url_image: string;
  action: string | null;
  target: string | null;
};

const BannerSlide = ({ url_image, action, target }: BannerSlideProps) => {
  const image = <img src={url_image} alt={action || ''} loading="lazy" />;

  if (!action) return image;

  const resolvedTarget = typeof target === 'object' ? undefined : target;

  return (
    <a
      href={action}
      target={resolvedTarget || '_blank'}
      rel="noopener noreferrer"
    >
      {image}
    </a>
  );
};

const FallbackBanner = () => {
  const event = useEventfy();

  if (!event.banner) return null;

  return (
    <S.Container>
      <img src={event.banner} alt={event.name} loading="lazy" />
    </S.Container>
  );
};

const LobbyBannerCarousel = () => {
  const banners = useEventfy(selectLobbyBanners);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [paused, setPaused] = useState(false);

  const goTo = useCallback((index: number) => setCurrentSlide(index), []);

  useEffect(() => {
    if (banners.length <= 1 || paused) return undefined;

    const duration = (banners[currentSlide]?.limit_time || 5) * 1000;
    const timer = setTimeout(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, duration);

    return () => clearTimeout(timer);
  }, [currentSlide, banners, paused]);

  if (!banners.length) return <FallbackBanner />;

  if (banners.length === 1) {
    return (
      <S.Container>
        <BannerSlide {...banners[0]} />
      </S.Container>
    );
  }

  return (
    <S.Container
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <S.Track $currentSlide={currentSlide} $total={banners.length}>
        {banners.map((banner) => (
          <S.Slide key={banner.id}>
            <BannerSlide {...banner} />
          </S.Slide>
        ))}
      </S.Track>
      <S.Dots>
        {banners.map((banner, index) => (
          <S.Dot
            key={banner.id}
            $active={index === currentSlide}
            onClick={() => goTo(index)}
          />
        ))}
      </S.Dots>
    </S.Container>
  );
};

export default LobbyBannerCarousel;
